import React, { FC, useEffect, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Space, Button, notification, Input } from 'antd'
import { CheckCircleOutlined, SmileOutlined } from  '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import * as go from 'gojs'
import { ReactDiagram } from 'gojs-react'
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
import { cloneDeep } from 'lodash'
import { getOrganList } from '@/services/organStructure.service'
import '@/pages/table2Mana/components/gojs.css'
import type { IAppState } from '@/store/reducers'
import type { IOrganPerson } from '@/store/models/organStructure.model'
import { API } from '@/config'
import defaultHeadImg from '@/assets/images/HSnopic.png'

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
type Icb = (item:any) => boolean

const popMsg = (msg: string, type?: string) => {
    const args = {
        message: msg,
        description: '',
        icon: type !== 'error' ? <CheckCircleOutlined style={{ color: '#08993f' }} />
            : <SmileOutlined style={{ color: '#e1931e' }} />,
        duration: 3,
    }
    notification.open(args)
}

function appendData(record: any, nodeDataArray: any[], parentNode: any, searchStatus?: boolean){
    nodeDataArray.push({
        ...record,
        parent: parentNode.id,
        key: record.id, // key 作为 from 和 to 的对应字段
        pic: record.avatar, // pic 每次刷新图片才能显示
        isTreeExpanded: searchStatus ? true : record.orgRole < 2 
    })
}

function appendSubChildren(record: any, nodeDataArray: any[], linkDataArray: { from: any; to: any }[], searchStatus?: boolean) {
    record.key = record.id
    record.pic = record.avatar
    record.children.forEach((item: any) => {
        appendData(item, nodeDataArray, record, searchStatus)
        linkDataArray.push({ from: record.id, to: item.id })
        if (item.children && item.children.length) {
            appendSubChildren(item, nodeDataArray, linkDataArray, searchStatus)
        }
    })
}

const OrganList: FC = () => {    
    const [organPersonList, setOrganPersonList] = useState<IOrganPerson[]>([])
    const [goObj, setGoObj] = useState<any>(null)
    const [searchKey, setSearchKey] = useState('')
    const [linkDataArray, setLinkDataArray] = useState<{ from: number; to: number; }[]>([])
    const searchStatus = useRef(false)
    const treeDataRes = useRef<any[]>([])
    const dataArrayRef = useRef<{
        list: IOrganPerson[]
        linkData: { from: number; to: number; }[]
    }>({ list: [], linkData: [] })
    const authInfo = useSelector((state: IAppState) => state.auth.authInfo)
    const { t, i18n } = useTranslation()
    const { Search } = Input

    function initDiagram(token: string) {
        const $ = go.GraphObject.make  // for conciseness in defining templates
      
        const myDiagram =
            $(go.Diagram, {
                allowCopy: false,
                allowDelete: false,
                //initialAutoScale: go.Diagram.Uniform,
                maxSelectionCount: 1, // users can select only one part at a time
                validCycle: go.Diagram.CycleDestinationTree, // make sure users can only create trees
                // 'clickCreatingTool.archetypeNodeData': { // allow double-click in background to create a new node
                //     name: '(new person)',
                //     title: '',
                //     comments: ''
                // },
                'clickCreatingTool.insertPart': function(loc: any) {  // override to scroll to the new node
                    const node = go.ClickCreatingTool.prototype.insertPart.call(this as any, loc)
                    if (node !== null) {
                        // this.diagram.select(node)
                        // this.diagram.commandHandler.scrollToPart(node)
                        // this.diagram.commandHandler.editTextBlock(node.findObject('NAMETB'))
                    }
                    return node
                },
                model: new go.GraphLinksModel(
                    {
                        linkKeyProperty: 'key'  // IMPORTANT! must be defined for merges and data sync when using GraphLinksModel
                    }
                ),
                layout: $(go.TreeLayout,
                    {
                        treeStyle: go.TreeLayout.StyleLastParents,
                        arrangement: go.TreeLayout.ArrangementHorizontal,
                        // properties for most of the tree:
                        angle: 90,
                        layerSpacing: 35,
                        // properties for the "last parents":
                        alternateAngle: 90,
                        alternateLayerSpacing: 35,
                        alternateAlignment: go.TreeLayout.AlignmentBus,
                        alternateNodeSpacing: 20
                    }
                ),
                'undoManager.isEnabled': false // enable undo & redo
            })
      
        const levelColors = ['#AC193D', '#2672EC', '#8C0095', '#5133AB',
            '#008299', '#D24726', '#008A00', '#094AB2'];
      
        // override TreeLayout.commitNodes to also modify the background brush based on the tree depth level
        (myDiagram.layout as any).commitNodes = function() {
            (go.TreeLayout.prototype as any).commitNodes.call(this);  // do the standard behavior
            // then go through all of the vertexes and set their corresponding node's Shape.fill
            // to a brush dependent on the TreeVertex.level value
            (myDiagram.layout as any).network.vertexes.each((v: any) => {
                if (v.node) {
                    const level = v.level % (levelColors.length)
                    const color = levelColors[level]
                    const shape = v.node.findObject('SHAPE')
                    if (shape) shape.stroke = $(go.Brush, 'Linear', {
                        0: color, 1: go.Brush.lightenBy(color, 0.05), start: go.Spot.Left, end: go.Spot.Right
                    })
                }
            })
        }
      
        // this is used to determine feedback during drags
        function mayWorkFor(node1: any, node2: any) {
            if (!(node1 instanceof go.Node)) return false  // must be a Node
            if (node1 === node2) return false  // cannot work for yourself
            if (node2.isInTreeOf(node1)) return false  // cannot work for someone who works for you
            return true
        }
      
        // This function provides a common style for most of the TextBlocks.
        // Some of these values may be overridden in a particular TextBlock.
        function textStyle() {
            return { font: '9pt  Segoe UI,sans-serif', stroke: 'white' }
        }
        function textStyle2() {
            return { font: '9pt  Segoe UI,sans-serif', stroke: 'white',
                verticalAlignment: go.Spot.Center,
                wrap: go.TextBlock.WrapBreakAll, width: 100 }
        }
      
        // This converter is used by the Picture.
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        function findHeadShot(pic: string) {
            if (!pic) return defaultHeadImg // There are only 16 images on the server
            return `${API}/api/pangooITMT/orgChart/avatar?path=${encodeURIComponent(pic)}&token=${token}`
        }
    
        // define the Node template
        myDiagram.nodeTemplate =
            $(go.Node, 'Spot',
                {
                    selectionObjectName: 'BODY',
                    mouseEnter: (e, node: any) => {
                        if (node.findObject('BUTTON') && node.findObject('BUTTONX')) {
                            node.findObject('BUTTON').opacity = node.findObject('BUTTONX').opacity = 1
                        }
                    },
                    mouseLeave: (e, node: any) => {
                        if (node.findObject('BUTTON') && node.findObject('BUTTONX')) {
                            node.findObject('BUTTON').opacity = node.findObject('BUTTONX').opacity = 0
                        }
                    },
                    // handle dragging a Node onto a Node to (maybe) change the reporting relationship
                    mouseDragEnter: (e, node: any) => {
                        const diagram = node.diagram
                        const selnode = diagram.selection.first()
                        if (!mayWorkFor(selnode, node)) return
                        const shape = node.findObject('SHAPE')
                        if (shape) {
                            shape._prevFill = shape.fill  // remember the original brush
                            shape.fill = 'darkred'
                        }
                    },
                    mouseDragLeave: (e, node: any) => {
                        const shape = node.findObject('SHAPE')
                        if (shape && shape._prevFill) {
                            shape.fill = shape._prevFill  // restore the original brush
                        }
                    },
                },
                // for sorting, have the Node.text be the data.name
                new go.Binding('text', 'name'),
                // bind the Part.layerName to control the Node's layer depending on whether it isSelected
                new go.Binding('layerName', 'isSelected', sel => sel ? 'Foreground' : '').ofObject(),
                $(go.Panel, 'Auto',
                    { name: 'BODY' },
                    // define the node's outer shape
                    $(go.Shape, 'Rectangle',
                        { name: 'SHAPE', fill: '#333333', stroke: 'white', strokeWidth: 2.5, portId: '' }),
                    $(go.Panel, 'Horizontal',
                        $(go.Picture,
                            {
                                name: 'Picture',
                                desiredSize: new go.Size(60, 70),
                                margin: 1.5,
                            },
                            // 字段名必须为 pic
                            new go.Binding('source', 'pic', findHeadShot)
                        ),
                        // define the panel where the text will appear
                        $(go.Panel, 'Table',
                            {
                                minSize: new go.Size(130, NaN),
                                maxSize: new go.Size(180, NaN),
                                margin: new go.Margin(6, 10, 0, 6),
                                defaultAlignment: go.Spot.Left
                            },
                            $(go.RowColumnDefinition, { column: 3, width: 4 }),
                            $(go.TextBlock, textStyle(),  // the name
                                {
                                    name: 'nickName',
                                    row: 0, column: 0, columnSpan: 4,
                                    font: '12pt Segoe UI,sans-serif',
                                    editable: false, isMultiline: false,
                                    minSize: new go.Size(50, 16),
                                    margin: new go.Margin(0, 0, 5, 0)
                                },
                                new go.Binding('text', 'nickName').makeTwoWay()),
                            $(go.TextBlock, `${t('WorkGroup')}: `, textStyle(),
                                { row: 1, column: 0, columnSpan: 1 }),
                            $(go.TextBlock, textStyle2(),
                                {
                                    row: 1, column: 1, columnSpan: 3,
                                    font: '8pt sans-serif',
                                    editable: false, isMultiline: true,
                                    minSize: new go.Size(50, 20),
                                    margin: new go.Margin(0, 0, 0, 10)
                                },
                                new go.Binding('text', 'workGroup',
                                    v => {
                                        return v ? v.workGroupName : `${t('Null Value')} `
                                    }
                                ).makeTwoWay(),
                            ),
                            $(go.TextBlock, `${t('orgRole')}: `, textStyle(),
                                { row: 2, column: 0, columnSpan: 1 }),
                            $(go.TextBlock, textStyle2(),  // the comments
                                {
                                    row: 2, column: 1, columnSpan: 3,
                                    font: '9pt sans-serif',
                                    wrap: go.TextBlock.WrapFit,
                                    editable: false,  // by default newlines are allowed
                                    minSize: new go.Size(100, 20),
                                    margin: new go.Margin(0, 0, 0, 10)
                                },
                                new go.Binding('text', 'orgRole',
                                    v => {
                                        return (['ITMT ChairMan', 'ITMT Leader', 'ITMT SubLeader', t('itmtUser'), 'TSP','PRG','SecretaryGeneral','CommitteeMembers','Co-ordinator'][v - 1] || '')
                                    }
                                ).makeTwoWay())
                        ) // end Table Panel
                    ) // end Horizontal Panel
                ), // end Auto Panel
            )  // end Node, a Spot Panel
        myDiagram.nodeTemplate.contextMenu =
            $('ContextMenu',
                '',
            )
        myDiagram.linkTemplate =
            $(go.Link, go.Link.Orthogonal,
                { layerName: 'Background', corner: 5 },
                $(go.Shape, { strokeWidth: 1.5, stroke: '#999' }))  // the link shape
    
        return myDiagram
    }

    const getOrganListFn = async () => {
        const res = await getOrganList()
        console.log('getOrganListFn: ', res)
        treeDataRes.current = res
        const _linkDataArray: { from: number; to: number; }[] = []
        if (res.length) {
            if (searchKey === '') {
                appendSubChildren(res[0], res, _linkDataArray)
                setLinkDataArray(_linkDataArray)
                setOrganPersonList([...res])
            } else {
                onChangeSearch(searchKey)
            }
        }
    }
    const onChangeSearch = (val: string) => {
        setSearchKey(val)
        const _linkDataArray: { from: number; to: number; }[] = []
        // 搜索时展示所有层级，为空条件时或初始加载时则只显示2级
        if (val === '') {
            searchStatus.current = false
            appendSubChildren(treeDataRes.current[0], treeDataRes.current, _linkDataArray, searchStatus.current)
            setLinkDataArray(_linkDataArray)
            setOrganPersonList([...treeDataRes.current])
            return
        }
        const result = searchTree(JSON.parse(JSON.stringify(treeDataRes.current)), item => 
            item.nickName.toLowerCase().indexOf(val.toLowerCase()) !== -1
        )
        if (result?.length) {
            searchStatus.current = true
            appendSubChildren(result[0], result, _linkDataArray, searchStatus.current)
            setLinkDataArray(_linkDataArray)
            setOrganPersonList([...result])
        } else {
            popMsg(`${t('searchNoUser')}\n(${t('searchUserPrompt')})`, 'error')
        }
    }
    const handleModelChange = async (e: any) => {
        console.log('handleModelChange: ', e)
        // if (isInit) {
        //     return
        // }
        // try {
        //     const res = await editOrganPerson(e.modifiedNodeData[0])
        //     console.log('res: ', res)
        // } catch (error: any) {
        //     const str = getErrorStr(error)
        //     dispatch(requestFail(str))
        // }
    }
    const zoomToFit = () => {
        goObj && goObj.commandHandler.zoomToFit()
    }
    const centerOnRoot = (key?: any) => {
        if (typeof key !== 'number') {
            key = organPersonList.length ? organPersonList[0].id : undefined
        }
        if (key && goObj) {
            goObj.scale = 1
            goObj.commandHandler.scrollToPart(goObj.findNodeForKey(key))
        }
    }
    const searchTree = (tree: any[], cb: Icb) => {
        let stack: any[] = []
        const mapObj: Record<string, any> = {}
        const resultObj: Record<string, any> = {}
        const resultObj2: Record<string, any> = {}
        let resultObj3: any
        stack = stack.concat(tree)
        while (stack.length) {
            const item = stack.shift()
            if (item.children) {
                const { id } = item
                mapObj[id] = item
                let findFlag = false
                item.children.forEach((subItem: any) => {
                    subItem.parentObj = item
                    mapObj[subItem.id] = subItem.parentObj
                    if (cb(subItem)) {
                        resultObj[subItem.id] = subItem
                        findFlag = true
                    }
                    stack.push(subItem)
                })
                if (!findFlag) {
                    item.children = []
                }
            }
        }
        let stack2: any[] = []
        stack2 = stack2.concat(Object.values(resultObj))
        while (stack2.length > 0) {
            const item = stack2.shift()
            if (item.children) {
                item.children.forEach((subItem: any) => {
                    stack2.push(subItem)
                })
            }
        }
        stack2 = stack2.concat(Object.values(resultObj))
        while (stack2.length > 0) {
            const item = stack2.shift()
            if (item.parentObj) {
                if (!resultObj2[item.parentObj.id]) {
                    item.parentObj.children = [item]
                    resultObj2[item.parentObj.id] = item.parentObj
                } else {
                    const findObj = item.parentObj.children.find((subItem: any) => subItem.id === item.id)
                    if (!findObj) {
                        item.parentObj.children.push(item)
                        // item.parentObj.children.sort((a: any, b: any) => a.id - b.id)
                    }
                }
                stack2.push(item.parentObj)
            } else {
                resultObj3 = item
            }
        }
        // console.log('mapObj: ', mapObj)
        if (resultObj3) {
            return [resultObj3]
        } else {
            return null
        }
    }
    useEffect(() => {
        getOrganListFn()
    }, [])
    useEffect(() => {
        dataArrayRef.current.linkData = linkDataArray
    }, [linkDataArray])
    useEffect(() => {
        dataArrayRef.current.list = organPersonList
    }, [organPersonList])

    return (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Space direction='vertical' style={{ position: 'relative', width: '100%' }}>
                <Space>
                    <Search style={{ width: 350 }} allowClear
                        placeholder={t('Please Select') + (i18n.language !== 'zh' ? ' ' : '') + t('organPerson') + (i18n.language !== 'zh' ? ' ' : '') + ', ' + t('organPersonTips')}
                        enterButton={t('search')}
                        onSearch={onChangeSearch}
                    />
                </Space>
                {authInfo && <ReactDiagram
                    initDiagram={() => {
                        let token = localStorage.getItem('userInfo')
                        if (token) {
                            token = JSON.parse(token).token
                        } else {
                            token = ''
                        }
                        const myDiagram = initDiagram(token as string)
                        console.log('myDiagram: ', myDiagram)
                        setGoObj(myDiagram)
                        return myDiagram
                    }}
                    divClassName='orgin-structure'
                    nodeDataArray={organPersonList}
                    linkDataArray={linkDataArray}
                    onModelChange={handleModelChange}
                />}
                <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button onClick={zoomToFit}>{t('Zoom To Fit')}</Button>
                    <Button onClick={centerOnRoot}>{t('Center on Root')}</Button>
                </Space>
            </Space>
        </Space>
    )
}

export default OrganList